import Error from 'enums/error';
import { getAccessToken, logoutCleanup } from 'helpers/auth';
import { getAssignedAcceptLanguage } from 'helpers/localePreference';
import { TModal } from 'interfaces/modal';
import {
  ApolloClient,
  ApolloLink,
  from,
  fromPromise,
  HttpLink,
  InMemoryCache,
  makeVar,
  ReactiveVar,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
// generated by Fragment Matcher plugin
import generatedIntrospection from '../introspection-result.json';
import { requestTokenRefresh } from './refreshToken';

enum Theme {
  GREEN = 'Theme--Green',
  BLUE = 'Theme--Blue',
}

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_API_BASE_URL as string,
});

const authLink = new ApolloLink((operation, forward) => {
  const token = getAccessToken();
  const acceptLanguage = getAssignedAcceptLanguage();
  operation.setContext({
    headers: {
      Authorization: token && `Bearer ${token}`,
      'X-Client': 'candidate',
      // Only override Accept-Language when the locale was system-assigned; an
      // explicitly selected locale is already authoritative server-side.
      ...(acceptLanguage ? { 'Accept-Language': acceptLanguage } : {}),
    },
  });

  return forward(operation);
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let refreshPromise: Promise<any> | null = null;

export const errorLink = onError(
  ({ graphQLErrors, networkError, forward, operation }) => {
    // TODO: Add proper error handling.
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        // eslint-disable-next-line no-console
        console.log(
          `[GraphQL error]: Message: ${err.message}, Location: ${err.locations}, Path: ${err.path}`
        );
        if (
          (err.extensions?.response?.statusCode === 401 ||
            err.extensions?.originalError?.statusCode === 401) &&
          operation.operationName !== 'RequestTokenRefresh'
        ) {
          if (!refreshPromise) {
            refreshPromise = requestTokenRefresh(client)
              // eslint-disable-next-line no-loop-func
              .finally(() => {
                refreshPromise = null;
              });
          }
          return fromPromise(refreshPromise)
            .filter(value => Boolean(value))
            .flatMap(() => {
              return forward(operation);
            });
        }

        if (
          operation.operationName === 'User' &&
          err.extensions?.exception?.response?.code ===
            Error.FIND_MANY_NOT_FOUND
        ) {
          logoutCleanup();
        }
      }
    }

    if (networkError) {
      // eslint-disable-next-line no-console
      console.log(`[Network error]: ${networkError}`);
    }

    return undefined;
  }
);

export const modalsVar: ReactiveVar<TModal[]> = makeVar<TModal[]>([]);
// Initialize theme var and sets initial theme to be "GEEN" theme.
export const themeVar: ReactiveVar<Theme> = makeVar<Theme>(Theme.GREEN);

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        modals: {
          read() {
            return modalsVar();
          },
        },
        theme: {
          read() {
            return themeVar();
          },
        },
        SkillsProfileOpenOne: {
          merge: false,
        },
      },
    },
  },
  possibleTypes: generatedIntrospection.possibleTypes,
});

const client = new ApolloClient({
  cache,
  link: from([errorLink, authLink, httpLink]),
});

export default client;
